import React, { useEffect, useState } from "react";
import {
  Box, Typography, CircularProgress, Table, TableBody, TableCell,
  TableContainer, TableHead, TableRow, Chip, Tooltip
} from "@mui/material";
import { api } from "../services/api";
import ArrowRightAltIcon from '@mui/icons-material/ArrowRightAlt';
import HistoryToggleOffIcon from "@mui/icons-material/HistoryToggleOff";
import GitStatusCell from "./instanceresize/GitStatus";

const STATUS_STYLES = {
  completed: { bg: "#dcfce7", fg: "#166534", dot: "#16a34a" },
  success: { bg: "#dcfce7", fg: "#166534", dot: "#16a34a" },
  failed: { bg: "#fee2e2", fg: "#991b1b", dot: "#dc2626" },
  rejected: { bg: "#fee2e2", fg: "#991b1b", dot: "#dc2626" },
  inprogress: { bg: "#fef3c7", fg: "#92400e", dot: "#f59e0b" },
  "in progress": { bg: "#fef3c7", fg: "#92400e", dot: "#f59e0b" },
  pending: { bg: "#e0e7ff", fg: "#3730a3", dot: "#6366f1" },
};

const StatusPill = ({ value }) => {
  const key = String(value || "").toLowerCase().trim();
  const s = STATUS_STYLES[key] || { bg: "#f1f5f9", fg: "#475569", dot: "#94a3b8" };
  return (
    <Chip
      size="small"
      label={value || "—"}
      sx={{
        bgcolor: s.bg,
        color: s.fg,
        fontWeight: 600,
        fontSize: "0.72rem",
        borderRadius: "6px",
        "& .MuiChip-label": { px: 1 },
      }}
    />
  );
};

const HEADERS = [
  { key: "applied", label: " " },
  { key: "snow", label: "Gsnow No#" },
  { key: "name", label: "Name" },
  { key: "volumeId", label: "Volume ID" },
  { key: "type", label: "Type" },
  { key: "size", label: "Size" },
  { key: "iops", label: "IOPS" },
  { key: "throughput", label: "Throughput" },
  { key: "price", label: "Price" },
  { key: "priceChange", label: "Price Change" },
  { key: "editStatus", label: "Edit Status" },
  { key: "gitStatus", label: "Git Status" },
  { key: "requestedBy", label: "Requested By" },
  { key: "requestedOn", label: "Requested On" },
];

export default function DiskEditHistory({ parentResourceId }) {
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!parentResourceId) return;
    setLoading(true);
    api.get(`/api/Resources/GetResourceVolumeEditHistory/${parentResourceId}`)
      .then(res => setHistory(res.data || []))
      .catch(err => console.error("Failed to load disk history", err))
      .finally(() => setLoading(false));
  }, [parentResourceId]);

  if (loading) {
    return <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}><CircularProgress /></Box>;
  }

  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    const correctedDateString = dateString.split('.')[0] + 'Z';
    const date = new Date(correctedDateString);
    if (Number.isNaN(date.getTime())) return 'Invalid Date';
    
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    const hours = String(date.getHours()).padStart(2, '0');
    const minutes = String(date.getMinutes()).padStart(2, '0');
    const seconds = String(date.getSeconds()).padStart(2, '0');

    return `${year}-${month}-${day} ${hours}:${minutes}:${seconds}`;
  };

  const formatTransition = (prev, next, unit = "", isPrice = false) => {
    if (prev == null && next == null) return "—";

    const formatValue = (val) => isPrice ? `$${Number(val).toFixed(2)}` : val;
    const displayUnit = unit ? ` ${unit}` : "";

    if (prev == null || prev === next) {
      return (
        <Typography sx={{ fontSize: "0.8rem", color: "#475569", whiteSpace: "nowrap" }}>
          {next != null ? `${formatValue(next)}${displayUnit}` : "—"}
        </Typography>
      );
    }

    return (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, flexWrap: 'nowrap', whiteSpace: "nowrap" }}>
        <Chip
          size="small"
          label={`${formatValue(prev)}${displayUnit}`}
          sx={{ bgcolor: "#f1f5f9", color: "#475569", fontWeight: 600, fontSize: "0.72rem", borderRadius: "6px" }}
        />
        <ArrowRightAltIcon sx={{ fontSize: 18, color: "#94a3b8" }} />
        <Chip
          size="small"
          label={`${formatValue(next)}${displayUnit}`}
          sx={{ bgcolor: "#eff6ff", color: "#1d4ed8", fontWeight: 700, fontSize: "0.72rem", borderRadius: "6px" }}
        />
      </Box>
    );
  };

  return (
    <Box sx={{ mt: 3 }}>
      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 2 }}>
        <Typography sx={{ fontSize: "0.95rem", fontWeight: 700, color: "#0f172a", px: 1 }}>
          Edit History
        </Typography>
   </Box>
      
      {history.length === 0 ? (
        <Box
          sx={{
            py: 6,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            gap: 1,
            border: "1px dashed #e2e8f0",
            borderRadius: "10px",
            bgcolor: "#f8fafc",
            mx: 1
          }}
        >
          <HistoryToggleOffIcon sx={{ fontSize: 32, color: "#94a3b8" }} />
          <Typography sx={{ fontSize: "0.875rem", fontWeight: 600, color: "#475569" }}>
            No existing Disk Edit history found
          </Typography>
          <Typography sx={{ fontSize: "0.75rem", color: "#94a3b8" }}>
            Submitted edit requests will appear here with their approval and Git status.
          </Typography>
        </Box>
      ) : (
        <TableContainer 
          sx={{ 
            border: "1px solid #e2e8f0", 
            borderRadius: "10px", 
            maxHeight: 420,
            mx: 1,
            width: 'auto',
            overflowX: 'auto'
          }}
        >
          <Table stickyHeader size="medium">
            <TableHead>
              <TableRow>
                {HEADERS.map((h) => (
                  <TableCell 
                    key={h.key}
                    sx={{
                      bgcolor: "#f8fafc",
                      fontSize: "0.72rem",
                      fontWeight: 700,
                      color: "#64748b",
                      letterSpacing: "0.4px",
                      whiteSpace: "nowrap",
                      borderBottom: "1px solid #e2e8f0",
                    }}
                  >
                    {h.label}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {history.map((row) => {
                // Using isUpdateSuccessful from your API payload to determine the circle color
                const isApplied = row.isUpdateSuccessful === true ||  String(row.isUpdateSuccessful).toLowerCase() === "true" ;

                const statusTooltip =
  String(row.editStatus ?? "").trim() ||
  (isApplied ? "Applied to Resource" : "Not Applied to Resource");

                return (
                  <TableRow 
                    key={row.volumeEditRequestId}
                    hover
                    sx={{ "&:last-child td": { borderBottom: 0 } }}
                  >
                    <TableCell sx={{ whiteSpace: "nowrap" }}>
                      <Tooltip title={statusTooltip} >       
                        <Box
                          sx={{
                            width: 10,
                            height: 10,
                            borderRadius: "50%",
                            bgcolor: isApplied ? "#16a34a" : "#dc2626",
                            mx: "auto",
                          }}
                        />
                      </Tooltip>
                    </TableCell>

                    <TableCell sx={{ whiteSpace: "nowrap" }}>
                      <Typography sx={{ fontSize: "0.8rem", fontWeight: 600, color:"#2563eb", whiteSpace: "nowrap" }}>
                        {row.snowChangeNumber || "—"}
                      </Typography>
                    </TableCell>
                    
                    <TableCell sx={{ fontSize: "0.8rem", color: "#475569", whiteSpace: "nowrap" }}>{row.volumeName || '—'}</TableCell>
                    <TableCell sx={{ fontSize: "0.8rem", color: "#475569", whiteSpace: "nowrap" }}>{row.volumeId || '—'}</TableCell>
                    <TableCell sx={{ fontSize: "0.8rem", color: "#475569", whiteSpace: "nowrap" }}>{row.volumeType || '—'}</TableCell>
                    <TableCell sx={{ whiteSpace: "nowrap" }}>{formatTransition(row.previousSize, row.newSize, 'GiB')}</TableCell>
                    <TableCell sx={{ whiteSpace: "nowrap" }}>{formatTransition(row.previousIOPS, row.newIOPS)}</TableCell>
                    <TableCell sx={{ whiteSpace: "nowrap" }}>{formatTransition(row.previousThroughput, row.newThroughput, 'MB/s')}</TableCell>
                    <TableCell sx={{ whiteSpace: "nowrap" }}>{formatTransition(row.previousPrice, row.newPrice, '', true)}</TableCell>
                    
                    <TableCell sx={{ whiteSpace: "nowrap" }}>
                      {row.priceDelta != null ? (
                        <Typography sx={{ fontSize: "0.8rem", fontWeight: 600, color: row.priceDelta > 0 ? "#dc2626" : row.priceDelta < 0 ? "#16a34a" : "#475569", whiteSpace: "nowrap" }}>
                          {row.priceDelta > 0 ? '+' : ''}${row.priceDelta.toFixed(2)} {row.priceDeltaStatus || ''}
                        </Typography>
                      ) : <Typography sx={{ fontSize: "0.8rem", color: "#475569", whiteSpace: "nowrap" }}>—</Typography>}
                    </TableCell>
                    
                    <TableCell sx={{ whiteSpace: "nowrap" }}>
                      <StatusPill value={row.editStatus} />
                    </TableCell>

                    <TableCell sx={{ whiteSpace: "nowrap" }}>
                      <GitStatusCell row={row} />
                    </TableCell>

                    <TableCell sx={{ whiteSpace: "nowrap" }}>
                      <Typography sx={{ fontSize: "0.8rem", color: "#475569", whiteSpace: "nowrap" }}>
                        {row.createdByName || row.createdBy || '—'}
                      </Typography>
                    </TableCell>
                    
                    <TableCell sx={{ whiteSpace: "nowrap" }}>
                      <Typography sx={{ fontSize: "0.8rem", color: "#475569", whiteSpace: "nowrap" }}>
                        {formatDate(row.createdOn)}
                      </Typography>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </Box>
  );
}